//
// Util functions
//
// Sections:
//
// SYNTAX
// MATHS
// OTHER
//

// Labels used in this file:
//
// @ASSUMPTION  - an assumption that is relied upon to work
// @BUG         - bug, or likely bug exists here
// @DEBUG       - in place for debug purposes
// @HACK        - hacky code follows
// @IMPLEMENT   - needs to be written
// @IMPROVE     - business logic could be added to, or improved
// @NB          - context to help understanding
// @OPTIMISE    - could be optimised
// @PERFORMANCE - performance bottleneck here
// @SECURITY    - potential security issue
// @TEMP        - should be deleted
// @TO DO       - needs attention
// @REFACTOR    - should be reworked
// @ROBUSTNESS  - brittle, should be improved
// @URGENT      - needs looking at urgently
//

// SYNTAX

/**
 * Turn an array of strings into an Enum-like object, e.g. [ 'FOO', 'BAR' ] > { FOO: 0, BAR: 1 }
 * @param {Array} arr
 * @returns {object}
 */
export const enumify = (arr) => {
    let result = {};
    for(let i = 0, n = arr.length; i < n; i++) {
        result[arr[i]] = i;
    }
    return result;
};

export const isUndefined = v => typeof v === 'undefined';
export const isDefined = v => !isUndefined(v);

export const isNullish = v => {
    if(v === null) return true;
    if(v === '') return true;
    return isUndefined(v);
};

export const isZeroish = n => Math.abs(0 - n) < 0.0000000001; // NB to 10th decimal place


export const partial = (fn, arg) => {
    return fn.bind(undefined, arg);
};

export const sortCompareDESC = (a, b) => {
    if(a > b) return -1;
    if(b > a) return 1;
    return 0;
};
export const sortCompareASC = (a, b) => sortCompareDESC(a, b) * -1;

// Get a complete, new copy of any objects and arrays containing other objects and arrays, retaining no references to
// the parent object or array
export const deepCopyByValue = async a => await JSON.parse(JSON.stringify(a));


// MATHS

export const first = arr => arr[0];
export const last = arr => arr[arr.length - 1];
export const inArray = (arr, item) => arr.indexOf(item) !== -1;
export const truncateAfter = (arr, len) => arr.slice(0, len);
export const pluckByIndex = (array_of_arrays, i) => array_of_arrays.map(arr => arr[i]);
export const pluckByKey = (array_of_objs, k) => array_of_objs.map(obj => obj[k]);

export const numToSINum = (num, digits) => {
    let prefix = num < 0 ? '-' : '';
    num = Math.abs(num);

    let si = [
        { value: 1E18, symbol: 'E' },
        { value: 1E15, symbol: 'P' },
        { value: 1E12, symbol: 'T' },
        { value: 1E9,  symbol: 'G' },
        { value: 1E6,  symbol: 'M' },
        { value: 1E3,  symbol: 'k' }
    ], rx = /\.0+$|(\.[0-9]*[1-9])0+$/, i;

    for (i = 0; i < si.length; i++) {
        if (num >= si[i].value) {
            return prefix + (num / si[i].value).toFixed(digits).replace(rx, '$1') + si[i].symbol;
        }
    }

    return prefix + num.toFixed(digits).replace(rx, '$1');
};


export const prettyPrintNum = (n) => {
    if (typeof n === 'undefined' || n === null) { return 0; }
    let parts = n.toString().split('.');

    if (parts.length > 1 && parts[1].length === 1) { parts[1] += '0'; }
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return parts[0];
};

export const prettyPrintNumEnglish = (n) => {
    let parts = n.toString().split('.');
    parts[0]  = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
};

export const prettyPrintRoundedNumEnglish = (n) => prettyPrintNum(Math.round(n));


export const min = arr => Math.min.apply(null, arr);
export const max = arr => Math.max.apply(null, arr);
export const sum = arr => {
    let result = 0;
    for(let i = 0, n = arr.length; i < n; i++) {
        result += arr[i];
    }
    return result;
};
export const avg = arr => sum(arr) / arr.length;
export const stdDev = arr => {
    const average = avg(arr);
    let sq_diffs = arr.map(v => Math.pow(v - average, 2));
    return Math.sqrt(avg(sq_diffs));
};


export const clamp = (min, max, n) => {
    if(n > max) return max;
    if(n < min) return min;
    return n;
};

/**
 * Get a new Array of a given length containing numbers from 0 to length - 1
 * @param {number} len length of desired Array
 * @returns {number[]} Array of numbers from 0..len-1
 */
export const range = (len = 1) => {
    let result = [];
    for(let i = 0; i < len; i++) {
        result.push(i);
    }
    return result;
};

/**
 * Get a new Array of a given length containing duplicates of provided value
 * @param {any} val item to fill array with
 * @param {number} len length of desired Array
 * @returns {any[]} Array of numbers from 0..len-1
 */
export const fillArray = (val = '', len = 1) => {
    let result = [];
    for(let i = 0; i < len; i++) {
        result.push(val);
    }
    return result;
};

export const randomIndexFrom = arr => {
    return Math.floor(Math.random() * (arr.length - 0.00001));
};
export const oneAtRandomFrom = arr => {
    return arr[randomIndexFrom(arr)];
};

export const chanceOfTrueOneIn = n => Math.random() > (1 - (1 / n));


// OTHER

export const zeroPad = (n) => {
    if(n >= 10) return n;
    return `0${n}`;
};


export const traverseDOMTreeAndGetNamedDataAttrVal = (dom_el, data_attr) => {
    if(dom_el.dataset[data_attr] !== undefined) return dom_el.dataset[data_attr];
    while(dom_el.parentNode !== null) {
        if(dom_el.dataset[data_attr] !== undefined) return dom_el.dataset[data_attr];
        dom_el = dom_el.parentNode;
    }
    return null;
};


export const csvToArray = (text) => {
    let p = '', row = [''], ret = [row], i = 0, r = 0, s = !0, l;
    for (l in text) {
        l = text[l];
        if ('"' === l) {
            if (s && l === p) row[i] += l;
            s = !s;
        } else if (',' === l && s) l = row[++i] = '';
        else if ('\n' === l && s) {
            if ('\r' === p) row[i] = row[i].slice(0, -1);
            row = ret[++r] = [l = '']; i = 0;
        } else row[i] += l;
        p = l;
    }
    return ret;
};


export const prettyHTMLFromPlainText = txt => {
    // Linebreaks
    txt = txt.replace(/(\r\n|\r|\n)/g, '<br>');
    // Links
    txt = txt.replace(/((http|https|ftp):\/\/[\w?=&.\/-;#~%-]+(?![\w\s?&.\/;#~%"=-]*>))/g,
        '<a href="$1" class="underline text-blue-600 hover:text-blue-800 visited:text-purple-600" target="_blank" rel="noopener noreferrer">$1</a>');
    return txt;
};


// https://community.cloudflare.com/t/convert-request-body-to-base64-encoded-string-solved/99341
export const base64Decode = (str) => {
    str = atob(str);
    const
        length = str.length,
        buf = new ArrayBuffer(length),
        bufView = new Uint8Array(buf);
    for (let i = 0; i < length; i++) { bufView[i] = str.charCodeAt(i) }
    return buf;
}

export const streamToBlob = async (stream, mimetype) => {
    if (mimetype != null && typeof mimetype !== 'string') {
        throw new Error('Invalid mimetype, expected string.')
    }
    const chunks = [];
    for await (let chunk of stream) {
        chunks.push(chunk);
    }
    return new Blob(chunks, { type: mimetype });
};

export const streamToFile = async (stream, filename, mimetype) => {
    if (mimetype != null && typeof mimetype !== 'string') {
        throw new Error('Invalid mimetype, expected string.')
    }
    const chunks = [];
    for await (let chunk of stream) {
        chunks.push(chunk);
    }
    return new File(chunks, filename,{ type: mimetype });
};


export const filenameWithoutExtension = filename => filename.substring(0, filename.lastIndexOf('.')) || filename;
export const fileExtensionFromFilename = filename => filename.substring(filename.lastIndexOf('.')) || '';


// URL Manipulation

/**
 * Get the value of a parameter from a URL Query String
 * @param {string} p parameter key
 * @param {?string} qs [optional] URL query string, will use current URL if not provided
 * @returns {string|null} parameter value or null if not present
 */
export const getParamFromQueryString = (p, qs = window.location.search) => {
    const params = new URLSearchParams(qs);
    return params.get(p);
};
/**
 * Get the value of a parameter from a URL
 * @param {string} p parameter key
 * @param {?string} url [optional] URL containing parameter, will use current URL if not provided
 * @returns {string|null} parameter value or null if not present
 */
export const getParamFromURL = (p, url = window.location) => {
    // NB using the quick trick of making an anchor tag and then
    const parser = document.createElement('a');
    parser.href = url;
    const qs = parser.search.substring(1);
    return getParamFromQueryString(p, qs);
};


export const buildQueryStringFromObj = data => {
    // If the data is already a string, return it as-is
    if (typeof (data) === 'string') return data;
    // Create a query array to hold the key/value pairs
    let query = [];
    // Loop through the data object
    for (var key in data) {
        if (data.hasOwnProperty(key)) {
            // Encode each key and value, concatenate them into a string, and push them to the array
            query.push(encodeURIComponent(key) + '=' + encodeURIComponent(data[key]));
        }
    }
    // Join each item in the array with a `&` and return the resulting string
    return query.join('&');
};
